import { SDKPageData } from "../_types";

// ─────────────────────────────────────────────────────────────────────────
// Refactored against a full read of the current majik-key.ts, types.ts,
// crypto-provider.ts, encryption-engine.ts, bitcoin.ts, solana.ts,
// constants.ts, wordlist.ts, README.md, and SECURITY.md.
//
// Two things flagged below with TODO need your confirmation before this
// goes live — everything else is grounded in verified source behavior.
// ─────────────────────────────────────────────────────────────────────────

export const JSON_SDK_MAJIK_KEY: SDKPageData = {
  id: "majik-key",
  metadata: {
    sdkName: "@majikah/majik-key",
    sdkSlug: "majik-key",
    tagline:
      "Post-quantum, multi-key cryptographic identity for the Majikah ecosystem",
    description:
      "A post-quantum ready seed phrase account library for the Majikah ecosystem. Deterministically derives a complete identity — X25519 encryption, ML-KEM-768 (FIPS-203) post-quantum key encapsulation, hybrid Ed25519 + ML-DSA-87 (FIPS-204) signing, and an experimental Bitcoin keypair — from a single BIP-39 mnemonic, with every private key encrypted at rest via Argon2id.",
    seo: "majik-key post-quantum BIP39 seed phrase identity ML-KEM-768 ML-DSA-87 Ed25519 X25519 Argon2id Bitcoin Solana JavaScript TypeScript",
    keywords: [
      "seed-phrase",
      "mnemonic",
      "bip39",
      "bip32",
      "bip84",
      "post-quantum",
      "ml-kem-768",
      "ml-dsa-87",
      "x25519",
      "ed25519",
      "argon2id",
      "cryptography",
      "key-derivation",
      "majikah",
      "identity",
      "hybrid-signing",
      "bitcoin",
      "solana",
      "security",
    ],
    category: "Cryptography",
    subcategory: "Identity & Key Management",
    language: "TypeScript",
    platforms: [
      "Node.js",
      "Browser",
      "Electron",
      "React Native",
      "Cloudflare Workers",
    ],
    photo: {
      logo: "https://storage.majikah.solutions/public/Logo_MajikUniversalID_Majikah_Thumbnail_WEBP.webp?url",
      banner:
        "https://storage.majikah.solutions/public/MajikUniversalID_SmallTile.jpg?url",
      screenshots: [],
      seo: "https://storage.majikah.solutions/public/Majikah_MajikUniversalID_SocialCard.webp?url",
    },
    badges: [
      {
        label: "Developed by",
        value: "Majikah Solutions OPC",
        type: "developed-by",
        url: "https://github.com/Majikah",
      },

      { label: "Version", value: "0.3.0", type: "version" },
      { label: "Downloads", value: "", type: "downloads" },
      { label: "Bundle Size", value: "", type: "bundle-size" },
      {
        label: "License",
        value: "Apache-2.0",
        type: "license",
        url: "https://opensource.org/licenses/Apache-2.0",
      },
      { label: "TypeScript", value: "Ready", type: "typescript" },
      { label: "Stars", value: "", type: "stars" },
      { label: "Language", value: "TypeScript", type: "language" },
      { label: "", value: "jedlsf", type: "sponsors" },
      { label: "Security", value: "ML-KEM-768 + ML-DSA-87", type: "custom" },
    ],
  },
  repository: {
    url: "https://github.com/Majikah/majik-key",
    owner: "Majikah",
    repo: "majik-key",
    branch: "main",
    license: "Apache-2.0",
  },
  hero: {
    title: "Majik Key",
    subtitle: "Post-Quantum, Multi-Key Cryptographic Identity",
    description:
      "Generate, encrypt, sign, recover, and migrate a complete cryptographic identity from a single BIP-39 mnemonic. Every account deterministically derives five keypairs — X25519 and ML-KEM-768 (NIST FIPS-203) for encryption, Ed25519 and ML-DSA-87 (NIST FIPS-204) for hybrid classical + post-quantum signing, and an experimental Bitcoin keypair — all protected by Argon2id at rest.",
    codeSnippet: {
      language: "typescript",
      code: "import { MajikKey } from '@majikah/majik-key';\n\n// Generate a new identity\nconst mnemonic = await MajikKey.generateMnemonic(); // 12/24-word BIP-39\nconst key = await MajikKey.create(mnemonic, 'your-passphrase', 'My Device');\n\nconsole.log(key.fingerprint);       // SHA-256 of X25519 public key\nconsole.log(key.isFullyUpgraded);   // true — Argon2id + ML-KEM-768\nconsole.log(key.hasSigningKeys);    // true — Ed25519 + ML-DSA-87\n\n// Lock and unlock\nkey.lock();\nawait key.unlock('your-passphrase');\nconst mlKemSk = key.getMlKemSecretKey(); // 2400-byte ML-KEM-768 secret key",
      caption:
        "Create a fully post-quantum capable identity, ready to sign and encrypt, in three lines.",
    },
    ctaButtons: [
      {
        label: "Install via npm",
        variant: "primary",
        url: "https://www.npmjs.com/package/@majikah/majik-key",
        external: true,
      },
      {
        label: "View on GitHub",
        variant: "secondary",
        url: "https://github.com/Majikah/majik-key",
        external: true,
      },
    ],
  },
  overview: {
    title: "What is Majik Key?",
    paragraphs: [
      "Majik Key is the identity layer of the Majikah ecosystem. A single `MajikKey` class turns a 12- or 24-word BIP-39 mnemonic into a fully deterministic, five-keypair cryptographic identity: X25519 for classical Diffie-Hellman key exchange, ML-KEM-768 for post-quantum key encapsulation, Ed25519 and ML-DSA-87 for hybrid classical + post-quantum signing, and — experimentally — a Bitcoin keypair.",
      "The X25519 identity key isn't generated independently — it's derived by converting the account's Ed25519 signing keypair via `ed2curve`. One Ed25519 keypair, two roles, both traceable back to the same 32 bytes of the BIP-39 seed. ML-KEM-768 draws on the full 64-byte seed for extra independent entropy, and ML-DSA-87 is derived from a domain-separated hash of that seed.",
      "Every keypair is recoverable from the mnemonic alone — no cloud backup required. Private keys are encrypted at rest using Argon2id (64 MB / 3 passes / 4 parallel lanes), WASM-accelerated when available with an automatic, bit-identical fallback to a pure-JS implementation.",
      "Majik Key handles the full lifecycle: creation, serialization, passphrase updates, locking/unlocking, legacy migration, and mnemonic-encrypted backup export and import — plus, experimentally, Bitcoin (BIP-32/84) and Solana key derivation for multi-chain use cases. It's designed to work in any JavaScript environment — Node.js, browsers, Electron, and Cloudflare Workers.",
    ],
    highlights: [
      {
        icon: "shield",
        label: "Post-Quantum Ready",
        description:
          "ML-KEM-768 (FIPS-203) for encryption and ML-DSA-87 (FIPS-204) for signing are both derived deterministically from the same BIP-39 mnemonic, alongside their classical counterparts.",
      },
      {
        icon: "pen",
        label: "Hybrid Classical + PQ Signing",
        description:
          "Ed25519 and ML-DSA-87 keypairs are derived together, powering Majik Signature's requirement that both a classical and a post-quantum signature pass verification.",
      },
      {
        icon: "key",
        label: "BIP-39 Mnemonic Recovery",
        description:
          "The entire five-keypair identity can be recovered from a mnemonic at any time, with no server dependency.",
      },
      {
        icon: "lock",
        label: "Argon2id at Rest",
        description:
          "Every private key is protected by Argon2id (64 MB / 3 passes / 4 lanes), WASM-accelerated via hash-wasm with a pure-JS fallback that produces bit-identical output.",
      },
      {
        icon: "refresh",
        label: "Seamless Migration",
        description:
          "Legacy accounts using PBKDF2 (v1) are fully upgraded — Argon2id, ML-KEM-768, Ed25519, and ML-DSA-87 — in a single `importFromMnemonicBackup()` call.",
      },
      {
        icon: "link",
        label: "Experimental Multi-Chain Keys",
        description:
          "Bitcoin (BIP-32/84) and Solana keys derive from the same mnemonic. Marked `@experimental` — API surface may change without a major version bump.",
      },
    ],
    whatItDoes: [
      "Generates BIP-39 mnemonics (128-bit or 256-bit entropy) across 10 supported wordlist languages",
      "Derives a deterministic X25519 identity keypair — converted via ed2curve from the same Ed25519 keypair used for signing",
      "Derives a deterministic ML-KEM-768 keypair from the full 64-byte BIP-39 seed",
      "Derives deterministic Ed25519 and ML-DSA-87 keypairs for hybrid classical + post-quantum signing",
      "@experimental Derives a domain-separated Bitcoin (BIP-32/84) keypair by default on new accounts, and can derive a Solana keypair on demand from the Ed25519 signing key",
      "Encrypts every private key at rest with Argon2id (v2), AES-256-GCM — or reads legacy PBKDF2 (v1) accounts",
      "Locks and unlocks identities in memory to minimize private key exposure",
      "Exports and imports mnemonic-encrypted backup blobs",
      "Migrates legacy (PBKDF2) accounts to Argon2id + the full modern keyset in one step via `importFromMnemonicBackup()`",
      "Serializes/deserializes identities to/from JSON for persistence — never in plaintext",
      "Converts to `MajikContact` and `MajikMessageIdentity` for use in the Majikah messaging stack",
    ],
    whatItDoesNot: [
      "Does not perform message encryption directly — use @majikah/majik-message for that",
      "Does not manage contacts or conversations",
      "Does not communicate with any server or relay",
      "Does not store keys — persistence is your application's responsibility",
      "Does not build chain-native Bitcoin/Solana transactions itself — the optional peer libraries (@scure/btc-signer, @solana/kit) handle address encoding, PSBTs, and kit-native signer objects",
    ],
    targetUsers: [
      {
        title: "Majikah App Developers",
        description:
          "Integrate identity management into apps built on Majik Message or Majik Signature. MajikKey produces the MajikContact and MajikMessageIdentity objects the messaging SDK expects, and the Ed25519/ML-DSA-87 keypairs Majik Signature signs with.",
        icon: "code",
      },
      {
        title: "Self-Custody App Builders",
        description:
          "Build apps where users own their cryptographic identity via a seed phrase — no accounts, no passwords stored on your server.",
        icon: "user",
      },
      {
        title: "Security-First Developers",
        description:
          "Applications that need quantum-resistant key exchange and signing today, with deterministic recovery and best-in-class KDF protection.",
        icon: "shield",
      },
      {
        title: "Multi-Chain Wallet Builders",
        description:
          "Experimental Bitcoin and Solana derivation gives wallet-style products (e.g. Majik Buwiz) a single mnemonic that spans messaging identity, signing, and on-chain keys.",
        icon: "link",
      },
    ],
  },
  installation: {
    title: "Installation",
    packageManagers: [
      { name: "npm", command: "npm install @majikah/majik-key" },
      { name: "yarn", command: "yarn add @majikah/majik-key" },
      { name: "pnpm", command: "pnpm add @majikah/majik-key" },
      { name: "bun", command: "bun add @majikah/majik-key" },
    ],
    verification: [
      {
        description:
          "Verify the install by generating a mnemonic in Node.js or your browser console:",
        code: "import { MajikKey } from '@majikah/majik-key';\nconsole.log(await MajikKey.generateMnemonic()); // logs a 12/24-word BIP-39 phrase",
        expectedOutput:
          "abandon abandon abandon abandon abandon abandon abandon abandon abandon abandon abandon about",
      },
    ],
  },
  quickStart: {
    title: "Quick Start",
    description:
      "The core workflows: creating a new identity, persisting it, unlocking it, and recovering it from a mnemonic backup.",
    steps: [
      {
        title: "1. Generate a mnemonic",
        description:
          "Create a cryptographically random 12/24-word BIP-39 mnemonic. Store it securely — it is the only thing needed to recover the full identity. `generateMnemonic()` is async.",
        code: {
          language: "typescript",
          code: "import { MajikKey } from '@majikah/majik-key';\n\nconst mnemonic = await MajikKey.generateMnemonic(); // 128-bit entropy, 12 words\n// or for 256-bit entropy:\nconst strongMnemonic = await MajikKey.generateMnemonic(256); // 24 words\n// or in another supported language:\nconst frenchMnemonic = await MajikKey.generateMnemonic(128, 'fr');",
        },
      },
      {
        title: "2. Create an identity",
        description:
          "Pass the mnemonic and a passphrase to `MajikKey.create()`. This derives the full keyset (X25519, ML-KEM-768, Ed25519, ML-DSA-87, and — by default — an experimental Bitcoin key) and encrypts every private key with Argon2id. The call is async because Argon2id is intentionally slow by design — expect it to take noticeably longer than a typical function call, more so without WASM acceleration.",
        code: {
          language: "typescript",
          code: "const key = await MajikKey.create(mnemonic, 'user-passphrase', 'My Laptop');\n\nconsole.log(key.id);              // fingerprint-based identity ID\nconsole.log(key.fingerprint);     // SHA-256 of X25519 public key (base64)\nconsole.log(key.publicKeyBase64); // X25519 public key (base64)\nconsole.log(key.hasMlKem);        // true\nconsole.log(key.hasSigningKeys);  // true — Ed25519 + ML-DSA-87\nconsole.log(key.isFullyUpgraded); // true — Argon2id + ML-KEM-768\nconsole.log(key.isUnlocked);      // true — private keys in memory after create()",
        },
        notes: [
          "The returned MajikKey is unlocked — private keys are held in memory.",
          "Call key.lock() immediately after use to clear all private key material from memory.",
          "Argon2id derivation uses WASM (hash-wasm) when available, falling back silently to @noble/hashes with bit-identical output.",
          // TODO(Zelijah): create() recently gained a Bitcoin opt-out option — confirm the exact parameter shape you shipped (e.g. a 5th `options?: { deriveBitcoin?: boolean }` argument) and update this note + the API reference table accordingly before publishing.
          "@experimental By default, create() also derives a domain-separated Bitcoin keypair. An opt-out option exists as of the latest version — check the current API reference for the exact parameter.",
        ],
      },
      {
        title: "3. Persist and reload",
        description:
          "Serialize to JSON for storage, then reconstruct with `MajikKey.fromJSON()`. The JSON never contains plaintext private key material — only passphrase-encrypted blobs.",
        code: {
          language: "typescript",
          code: "// Serialize (safe to store — no plaintext private keys)\nconst json = key.toJSON();\nawait myStorage.save('identity', json);\n\n// Later: reconstruct from JSON (identity is locked)\nconst loaded = MajikKey.fromJSON(await myStorage.load('identity'));\nconsole.log(loaded.isLocked); // true\n\n// Unlock with passphrase to access private keys\nawait loaded.unlock('user-passphrase');\nconst privateKey = loaded.getPrivateKey();\nconst mlKemSk = loaded.getMlKemSecretKey();\nconst edSk = loaded.getEdSecretKey();\nconst mlDsaSk = loaded.getMlDsaSecretKey();\nloaded.lock(); // clear everything from memory when done",
        },
      },
      {
        title: "4. Export and import a mnemonic backup",
        description:
          "Export a portable encrypted backup blob. Anyone with the mnemonic can recover the full identity — including the ML-KEM-768, Ed25519, and ML-DSA-87 keypairs — on any device.",
        code: {
          language: "typescript",
          code: "// Export backup (requires unlocked key)\nconst backup = await key.exportMnemonicBackup(mnemonic);\n// backup is a base64-encoded encrypted blob\n\n// Import on another device (performs full migration automatically)\nconst recovered = await MajikKey.importFromMnemonicBackup(\n  backup,\n  mnemonic,\n  'user-passphrase',\n  'New Device'\n);\nconsole.log(recovered.isFullyUpgraded); // true — Argon2id + ML-KEM-768 guaranteed\nconsole.log(recovered.hasSigningKeys);  // true — Ed25519 + ML-DSA-87 guaranteed",
        },
        notes: [
          "importFromMnemonicBackup() is also the correct upgrade path for old PBKDF2 accounts — it always re-derives the full identity fresh from the mnemonic.",
          "The backup blob is encrypted with a key derived from the mnemonic, not the passphrase — it's a mnemonic-verification blob, not a general-purpose private key backup.",
          "For a plaintext, transport-only export of the mnemonic itself, see toMnemonicJSON() in the API reference — handle its output with the same care as the mnemonic.",
        ],
      },
    ],
  },
  features: [
    {
      id: "five-keypair-derivation",
      title: "Deterministic Five-Keypair Derivation",
      description:
        "Every identity contains five cryptographic keypairs, all derived deterministically from a single BIP-39 mnemonic — your entire cryptographic identity is recoverable from 12 or 24 words.",
      icon: "key",
      content: [
        "The BIP-39 mnemonic is converted to a 64-byte seed via mnemonicToSeed() (PBKDF2-SHA512 per the BIP-39 spec).",
        "seed[0..32] generates an Ed25519 keypair. That same Ed25519 keypair is used directly for classical signing, and separately converted to X25519 via ed2curve for the encryption/identity keypair — one Ed25519 keypair, two roles.",
        "The full 64-byte seed is passed to ml_kem768.keygen(seed). ML-KEM uses seed[0..32] for lattice key matrix generation and seed[32..64] for the implicit rejection parameter z — independent entropy beyond the X25519/Ed25519 key.",
        'ML-DSA-87 is derived from a domain-separated hash: SHA-256(seed || "MajikSignatureSeedDSA"), then ml_dsa87.keygen() on that 32-byte result.',
        "@experimental The Bitcoin keypair uses real BIP-32 HD derivation off the raw 64-byte seed (HDKey.fromMasterSeed(seed).derive(path)), using a Majik-specific domain-separated path by default.",
        "The X25519 public key is SHA-256 hashed to produce the fingerprint, which doubles as the account ID.",
      ],
      table: {
        headers: [
          "Keypair",
          "Algorithm",
          "Seed Input",
          "Public Key Size",
          "Private Key Size",
          "Purpose",
        ],
        rows: [
          {
            cells: [
              "X25519",
              "Ed25519 → ed2curve → X25519",
              "seed[0..32]",
              "32 bytes",
              "32 bytes",
              "Classical DH, fingerprint, identity",
            ],
          },
          {
            cells: [
              "Ed25519",
              "Ed25519",
              "seed[0..32]",
              "32 bytes",
              "64 bytes",
              "Classical signing",
            ],
          },
          {
            cells: [
              "ML-KEM-768",
              "NIST FIPS-203 (Kyber)",
              "seed[0..64] (full)",
              "1184 bytes",
              "2400 bytes",
              "Post-quantum key encapsulation",
            ],
          },
          {
            cells: [
              "ML-DSA-87",
              "NIST FIPS-204 (Dilithium)",
              "hash(seed || domain string)",
              "2592 bytes",
              "4896 bytes",
              "Post-quantum signing",
            ],
          },
          {
            cells: [
              "Bitcoin (secp256k1)",
              "BIP-32 / BIP-84",
              "seed[0..64] (full)",
              "33 bytes (compressed)",
              "32 bytes",
              "@experimental — multi-chain use cases",
            ],
          },
        ],
      },
    },
    {
      id: "hybrid-signing",
      title: "Hybrid Classical + Post-Quantum Signing",
      description:
        "Ed25519 and ML-DSA-87 are derived as a pair on every account, giving you both a classical and a post-quantum signing key from the same mnemonic — the foundation Majik Signature builds on.",
      icon: "pen",
      content: [
        "Ed25519 is derived directly from seed[0..32] — the same keypair used to derive the X25519 identity key.",
        "ML-DSA-87 (FIPS-204) is derived from a domain-separated hash of the full seed, independent of the Ed25519/X25519 material.",
        "Both secret keys are encrypted at rest as separate AES-256-GCM blobs, sharing the account's Argon2id-derived key but each with its own random IV.",
        "Majik Signature consumes both keypairs and requires both signatures to verify — hybrid security against a future break of either scheme alone.",
      ],
      codeExample: {
        title: "Accessing signing keys",
        language: "typescript",
        code: "console.log(key.edPublicKey);     // Ed25519 public key, 32 bytes\nconsole.log(key.mlDsaPublicKey);  // ML-DSA-87 public key, 2592 bytes\nconsole.log(key.hasSigningKeys);  // true if both are present\n\nconst edSk = key.getEdSecretKey();       // throws if locked or absent\nconst mlDsaSk = key.getMlDsaSecretKey(); // throws if locked or absent",
      },
    },
    {
      id: "argon2id-key-protection",
      title: "Argon2id Key Protection (KDF v2)",
      description:
        "Private keys are never stored in plaintext. Every one of them is encrypted with AES-256-GCM using a key derived from your passphrase via Argon2id — one of the most memory-hard KDFs available.",
      icon: "lock",
      content: [
        "A fresh 32-byte random salt is generated for each identity and each passphrase change.",
        "Argon2id derives a 32-byte AES key from the passphrase + salt using parameters: m=65536 KB (64 MB memory), t=3 passes, p=4 parallel lanes.",
        "X25519, ML-KEM-768, Ed25519, ML-DSA-87, and (if present) the Bitcoin private key are each encrypted as a separate AES-256-GCM blob with its own random 12-byte IV — but all share the same Argon2id-derived key, so one KDF computation unlocks every key type at once.",
        "WASM acceleration via hash-wasm is used when available. On first use, the library probes WASM availability and falls back silently to @noble/hashes' pure-JS implementation. Both produce identical output.",
        "The previous KDF (PBKDF2-SHA256, v1) is kept for read-only decryption of legacy accounts. No new accounts are created with PBKDF2.",
      ],
      table: {
        headers: [
          "Parameter",
          "KDF v1 (PBKDF2, legacy)",
          "KDF v2 (Argon2id, current)",
        ],
        rows: [
          { cells: ["Algorithm", "PBKDF2-SHA256", "Argon2id"] },
          { cells: ["Iterations / Passes", "250,000", "3 passes"] },
          { cells: ["Memory Cost", "None", "64 MB (65536 KB)"] },
          { cells: ["Parallelism", "None", "4 lanes"] },
          { cells: ["Output", "32 bytes", "32 bytes"] },
          { cells: ["Status", "Read-only (legacy)", "Active (all new keys)"] },
        ],
      },
    },
    {
      id: "lock-unlock-model",
      title: "Lock / Unlock Memory Model",
      description:
        "Private key material is only held in memory when explicitly unlocked. This minimizes the window of exposure — especially important in long-lived Electron or server-side Node.js processes.",
      icon: "shield",
      content: [
        "After MajikKey.fromJSON(), the identity is locked — no private key bytes in memory.",
        "Calling unlock(passphrase) performs one Argon2id derivation, then decrypts every present key type's blob using that same derived key.",
        "Calling lock() clears the X25519, ML-KEM-768, Ed25519, ML-DSA-87, and Bitcoin secret keys, plus any cached experimental Solana keypair material, from the instance — no private material remains.",
        "All methods that require private key access (getPrivateKey(), getMlKemSecretKey(), getEdSecretKey(), getMlDsaSecretKey(), getBtcSecretKey(), toKeyIdentity(), etc.) throw a MajikKeyError if called while locked.",
        "After MajikKey.create(), the key starts in the unlocked state for immediate use. Call lock() as soon as the private keys are no longer needed.",
        "JavaScript cannot guarantee immediate, forced memory erasure — lock() drops references so key material becomes eligible for garbage collection, which is a best-effort reduction of exposure, not a hard security boundary against an attacker already running code in the same process.",
      ],
      codeExample: {
        title: "Lock/Unlock Pattern",
        language: "typescript",
        code: "const key = MajikKey.fromJSON(storedJson); // locked\nconsole.log(key.isLocked);   // true\nconsole.log(key.isUnlocked); // false\n\n// Unlock to use private keys\nawait key.unlock('passphrase');\nconst sk = key.getMlKemSecretKey(); // Uint8Array, 2400 bytes\nconst xsk = key.getPrivateKeyBase64(); // X25519 secret, base64\n\n// Lock immediately after use\nkey.lock();\nconsole.log(key.isLocked); // true\n\n// Attempting access while locked throws:\ntry {\n  key.getPrivateKey();\n} catch (e) {\n  console.error(e.message); // \"MajikKey is locked. Call unlock() first.\"\n}",
      },
    },
    {
      id: "mnemonic-backup",
      title: "Mnemonic-Encrypted Backup vs. Plaintext Seed Export",
      description:
        "Two different export formats exist for two different jobs — mixing them up is the easiest way to accidentally store a mnemonic in plaintext.",
      icon: "archive",
      content: [
        "exportMnemonicBackup() / the backup field produce a base64-encoded, encrypted verification blob: the X25519 private key encrypted with a key derived from the mnemonic itself (not the passphrase), plus the public key and fingerprint. It exists primarily so importFromMnemonicBackup() can verify a supplied mnemonic is correct before doing the (expensive) full re-derivation — not as a general-purpose private key backup.",
        "toMnemonicJSON(mnemonic, passphrase?), by contrast, is a plaintext transport format: the raw mnemonic words, and optionally the passphrase, embedded directly in the returned object. This is not encrypted at rest and should be treated exactly like the mnemonic itself.",
        "On import via importFromMnemonicBackup(), the backup is first decrypted to verify mnemonic correctness, then the full identity is re-derived from the mnemonic — guaranteeing ML-KEM-768, Ed25519, and ML-DSA-87 keys are always present, even in backups created before those key types existed.",
        "The backup format is self-describing: backupKdfVersion inside the blob tells the library whether to use Argon2id or legacy PBKDF2 for decryption.",
      ],
      codeExample: {
        title: "Backup Export & Import",
        language: "typescript",
        code: "// Encrypted verification blob (safe-ish; still requires the mnemonic to decrypt)\nconst backup = await key.exportMnemonicBackup(mnemonic);\n\n// ---- On another device ----\nconst recovered = await MajikKey.importFromMnemonicBackup(\n  backup,\n  mnemonic,\n  'newPassphrase',\n  'iPhone 15'\n);\nconsole.log(recovered.isFullyUpgraded); // true\nconsole.log(recovered.hasSigningKeys);  // true\n\n// ⚠️ Plaintext transport format — NOT for at-rest storage:\nconst mnemonicJson = key.toMnemonicJSON(mnemonic, 'password123');\n// mnemonicJson.seed is the raw mnemonic, split into words, in plaintext.",
      },
    },
    {
      id: "legacy-migration",
      title: "Automatic Legacy Migration",
      description:
        "Accounts created before Argon2id and the post-quantum keypairs were introduced (PBKDF2, kdfVersion: 1, no ML-KEM/Ed25519/ML-DSA) are fully upgraded in a single call.",
      icon: "refresh",
      content: [
        "importFromMnemonicBackup() is the full migration path. It verifies the backup (proving the mnemonic is correct), then re-derives the entire identity fresh from the mnemonic — including ML-KEM-768, Ed25519, and ML-DSA-87.",
        "There is no partial migration state at this level. An account is either fully upgraded (Argon2id + ML-KEM-768) or not yet upgraded — key.isFullyUpgraded reflects this.",
        "The migrate(passphrase) method upgrades the KDF from PBKDF2 to Argon2id only, without changing the passphrase or mnemonic, and without adding ML-KEM/Ed25519/ML-DSA/Bitcoin keys — use importFromMnemonicBackup() for the complete upgrade.",
        "Old PBKDF2-encrypted blobs are never written again after migration. Once upgraded, all re-encryptions use Argon2id.",
      ],
      table: {
        headers: [
          "Migration Path",
          "Method",
          "Adds ML-KEM / Signing Keys?",
          "Changes Passphrase?",
          "Requires Mnemonic?",
        ],
        rows: [
          {
            cells: [
              "KDF only (PBKDF2 → Argon2id)",
              "migrate(passphrase)",
              "No",
              "No",
              "No",
            ],
          },
          {
            cells: [
              "Full upgrade (KDF + full keyset)",
              "importFromMnemonicBackup()",
              "Yes",
              "Optional (set new one)",
              "Yes",
            ],
          },
          {
            cells: [
              "Passphrase change only",
              "updatePassphrase(old, new)",
              "No (preserves existing)",
              "Yes",
              "No",
            ],
          },
        ],
      },
    },
    {
      id: "experimental-web3",
      title: "Experimental Web3 Key Derivation",
      description:
        "Bitcoin and Solana key material derives from the same mnemonic. Marked @experimental throughout the source — the shape of this API may change without a major version bump.",
      icon: "link",
      content: [
        "Bitcoin key derivation is automatic on new accounts: real BIP-32 HD derivation off the raw 64-byte seed, using Majik's domain-separated path by default. WIF export and message signing (ECDSA or Schnorr) work with no extra dependency. Native SegWit address encoding requires the optional @scure/btc-signer peer dependency.",
        "Pass { standard: true } to derive the real BIP-84 mainnet path instead — the address any standard wallet would show for the same mnemonic — via MajikKey.deriveStandardBitcoinFromMnemonic() or getBitcoinKeypairMaterial({ standard: true }).",
        "Solana key material is not stored — it's derived on demand from the account's Ed25519 signing key (domain-separated via SHA256(edSeed || \"MajikMessageSolanaSeed\")) each time key.web3.solana is accessed, and cached in memory while the account is unlocked. The base58 address works with no extra dependency; a real @solana/kit signer requires the optional @solana/kit peer dependency.",
        "An opt-in reuseMessageKey option lets you reuse the Ed25519 signing key directly as the Solana key instead of the domain-separated derivation — not recommended, since it means one private key secures two different protocols.",
      ],
      table: {
        headers: ["Chain", "Peer Dependency", "Needed For"],
        rows: [
          {
            cells: [
              "Bitcoin",
              "@scure/btc-signer",
              "Native SegWit (bech32) address encoding, PSBT construction",
            ],
          },
          {
            cells: [
              "Solana",
              "@solana/kit",
              "Real KeyPairSigner instances, kit-native Address type",
            ],
          },
        ],
      },
      codeExample: {
        title: "Web3 usage",
        language: "typescript",
        code: "// Bitcoin — key material already derived and stored by default\nconsole.log(key.getBitcoinWIF());\nconsole.log(await key.web3?.bitcoin?.getBitcoinAddress()); // needs @scure/btc-signer\n\n// Solana — derived on demand from the Ed25519 signing key\nconsole.log(key.getSolanaAddress()); // no extra dependency\nconst solanaSigner = await key.getSolanaKeypair(); // needs @solana/kit",
      },
    },
    {
      id: "serialization",
      title: "JSON Serialization",
      description:
        "MajikKey instances serialize to a well-defined JSON structure. The serialized form is safe to persist — it contains no plaintext private key material.",
      icon: "file",
      content: [
        "key.toJSON() returns a MajikKeyJSON object: id, label, publicKey (base64), fingerprint, encryptedPrivateKey (base64), salt (base64), backup (base64), timestamp (ISO 8601), kdfVersion, and — where present — mlKemPublicKey/encryptedMlKemSecretKey, edPublicKey/encryptedEdSecretKey, mlDsaPublicKey/encryptedMlDsaSecretKey, and (experimental) btcPublicKey/encryptedBtcSecretKey.",
        "MajikKey.fromJSON(json) reconstructs a locked MajikKey from this object. No passphrase is required at this stage.",
        "key.toString(pretty?) is equivalent to JSON.stringify(key.toJSON(), null, pretty ? 2 : 0).",
        "Every secret key field is encrypted in the JSON — only public keys are stored in plaintext.",
        "The kdfVersion field tells the library which KDF to use on the next unlock() call. Absent/undefined is treated as legacy PBKDF2 (v1) for backwards compatibility.",
        "⚠️ toDangerousJSON() / fromDangerousJSON() are a separate, intentionally unencrypted export for server-side secret injection only — see the Security Policy for scope.",
      ],
      codeExample: {
        title: "MajikKeyJSON shape",
        language: "typescript",
        code: "const json = key.toJSON();\n/*\n{\n  id: string,\n  label: string,\n  publicKey: string,                    // base64 X25519 public key\n  fingerprint: string,\n  encryptedPrivateKey: string,           // base64 AES-GCM(Argon2id(passphrase, salt), X25519 sk)\n  salt: string,\n  backup: string,\n  timestamp: string,                     // ISO 8601\n  kdfVersion: 2,                         // 1 = PBKDF2 (legacy), 2 = Argon2id (current)\n  mlKemPublicKey?: string,\n  encryptedMlKemSecretKey?: string,\n  edPublicKey?: string,\n  encryptedEdSecretKey?: string,\n  mlDsaPublicKey?: string,\n  encryptedMlDsaSecretKey?: string,\n  btcPublicKey?: string,                 // @experimental\n  encryptedBtcSecretKey?: string,        // @experimental\n  mnemonicLanguage?: string\n}\n*/",
      },
    },
    {
      id: "ecosystem-interop",
      title: "Majikah Ecosystem Interoperability",
      description:
        "MajikKey integrates directly with the rest of the Majikah SDK stack via conversion helpers.",
      icon: "link",
      content: [
        "key.toContact() returns a MajikContact — the contact representation used across the Majikah messaging SDK. Includes X25519, ML-KEM, and (where present) Ed25519/ML-DSA public keys.",
        "key.toKeyIdentity() returns a MajikKeyIdentity — the raw identity object with live key material, used internally and as the basis for backup export.",
        "key.toSerializedIdentity() returns a minimal SerializedIdentity — useful for transport or simple identity assertions.",
        "key.toMajikMessageIdentity(user, options?) returns a MajikMessageIdentity — the identity container used by the Majik Message relay. Requires an existing MajikUser instance from @thezelijah/majik-user.",
      ],
      codeExample: {
        title: "Convert to Majikah ecosystem types",
        language: "typescript",
        code: "// Convert to MajikContact (for messaging SDK)\nconst contact = key.toContact();\nconst serialized = await contact.toJSON();\n\n// Convert to MajikMessageIdentity (for relay/backend registration)\nconst identity = await key.toMajikMessageIdentity(majikUser, {\n  label: 'My iPhone',\n  restricted: false\n});",
      },
    },
  ],
  useCases: [
    {
      title: "End-to-End Encrypted Messaging App",
      description:
        "Use MajikKey to create and manage user identities in a messaging app. Pair with @majikah/majik-message for full post-quantum E2EE.",
      scenario:
        "A user installs your app, generates a mnemonic, creates a MajikKey, and registers their identity with the relay. Their ML-KEM-768 and X25519 public keys are published. Other users encapsulate shared secrets against the ML-KEM public key when sending messages.",
      benefits: [
        "No username or email required — identity is the public key fingerprint",
        "Full recovery from a mnemonic on any device",
        "Post-quantum secure from day one",
      ],
      icon: "message",
    },
    {
      title: "Hybrid Post-Quantum File Signing",
      description:
        "Use the Ed25519 and ML-DSA-87 keypairs to sign files, requiring both a classical and a post-quantum signature to verify. This is exactly what Majik Signature builds on.",
      scenario:
        "An unlocked MajikKey signs a document with both keypairs. Verifiers check both signatures independently — a forged document would need to break both Ed25519 and ML-DSA-87 to pass.",
      benefits: [
        "Forward secrecy against a future break of either signature scheme alone",
        "Same mnemonic that recovers messaging identity also recovers signing keys",
        "No separate key management for signing vs. encryption",
      ],
      icon: "pen",
    },
    {
      title: "Offline File Encryption",
      description:
        "Derive an identity and use the X25519 or ML-KEM keypairs to encrypt files for a recipient without any server involvement.",
      scenario:
        "Alice creates a MajikKey and shares her public keys (as a MajikContact JSON). Bob uses those keys to encrypt a file. Alice decrypts with her private keys after unlocking her MajikKey. Everything happens locally.",
      benefits: [
        "Zero server dependency for the encryption step",
        "Recoverable identity from mnemonic — no credential vault needed",
        "ML-KEM-768 makes ciphertexts resistant to future quantum decryption",
      ],
      icon: "file-lock",
    },
    {
      title: "Multi-Chain Wallet Custody (Experimental)",
      description:
        "Build a wallet-style product where a single mnemonic spans messaging identity, signing, and on-chain Bitcoin/Solana keys — the model behind Majik Buwiz.",
      scenario:
        "A user's MajikKey is unlocked once. Its Ed25519/ML-DSA-87 keys handle signing, its X25519/ML-KEM-768 keys handle encrypted messaging, and its experimental Bitcoin/Solana keys handle on-chain custody — all recoverable from the same 12-24 words.",
      benefits: [
        "One recovery phrase for the entire multi-protocol identity",
        "No extra key management surface for adding chains",
        "Bitcoin WIF and Solana base58 address work without any extra install",
      ],
      icon: "link",
    },
    {
      title: "Migrating Legacy Accounts",
      description:
        "If your users have existing accounts created with PBKDF2 (kdfVersion: 1) and no post-quantum keys, call importFromMnemonicBackup() to upgrade them transparently.",
      scenario:
        "On first login after upgrading your app, detect key.isFullyUpgraded === false. Prompt the user for their mnemonic, call importFromMnemonicBackup(), and save the upgraded JSON. The user never needs to change their mnemonic.",
      benefits: [
        "Single function call handles the full migration",
        "No data loss — same fingerprint and identity ID preserved",
        "User experience is a one-time prompt for the mnemonic",
      ],
      codeExample: {
        title: "Migration flow",
        language: "typescript",
        code: "const existing = MajikKey.fromJSON(storedJson);\n\nif (!existing.isFullyUpgraded) {\n  const mnemonic = await promptUserForMnemonic();\n\n  const upgraded = await MajikKey.importFromMnemonicBackup(\n    existing.backup,\n    mnemonic,\n    currentPassphrase,\n    existing.label\n  );\n\n  await myStorage.save('identity', upgraded.toJSON());\n  console.log(upgraded.isFullyUpgraded); // true\n  console.log(upgraded.hasSigningKeys);  // true\n}",
      },
      icon: "refresh",
    },
  ],
  examples: [
    {
      title: "Full Lifecycle",
      description:
        "All major operations from generation through backup and recovery.",
      category: "Basic",
      difficulty: "beginner",
      examples: [
        {
          title: "Generate, Create, Serialize, Unlock",
          language: "typescript",
          code: "import { MajikKey } from '@majikah/majik-key';\n\n// 1. Generate mnemonic\nconst mnemonic = await MajikKey.generateMnemonic(); // 12 words\n\n// 2. Create identity\nconst key = await MajikKey.create(mnemonic, 's3cur3P@ss', 'Dev Laptop');\nconsole.log(key.fingerprint);      // base64 SHA-256 of X25519 pubkey\nconsole.log(key.isUnlocked);       // true\nconsole.log(key.isFullyUpgraded);  // true\nconsole.log(key.hasSigningKeys);   // true\n\n// 3. Persist\nconst serialized = key.toJSON();\n// ... save serialized to IndexedDB, Supabase, filesystem, etc.\n\n// 4. Reload and unlock\nconst loaded = MajikKey.fromJSON(serialized);\nconsole.log(loaded.isLocked); // true\nawait loaded.unlock('s3cur3P@ss');\nconsole.log(loaded.isUnlocked); // true\n\n// 5. Access keys\nconst mlKemPk = loaded.mlKemPublicKey;         // Uint8Array, 1184 bytes\nconst mlKemSk = loaded.getMlKemSecretKey();    // Uint8Array, 2400 bytes\nconst edSk    = loaded.getEdSecretKey();       // Uint8Array, 64 bytes\nconst mlDsaSk = loaded.getMlDsaSecretKey();    // Uint8Array, 4896 bytes\nconst xPk     = loaded.publicKeyBase64;        // string (base64)\nconst xSk     = loaded.getPrivateKeyBase64();  // string (base64)\n\n// 6. Lock when done\nloaded.lock();",
        },
      ],
    },
    {
      title: "Passphrase & Label Management",
      description: "Changing the passphrase and updating the device label.",
      category: "Basic",
      difficulty: "beginner",
      examples: [
        {
          title: "Update passphrase and label",
          language: "typescript",
          code: "// Change passphrase (re-derives Argon2id, fresh salt, re-encrypts every key type present)\nconst key = MajikKey.fromJSON(storedJson);\nawait key.updatePassphrase('oldPass', 'newPass');\nawait myStorage.save('identity', key.toJSON()); // persist updated blob\n\n// Update label (synchronous)\nkey.updateLabel('Work MacBook');\nconsole.log(key.label); // 'Work MacBook'\n\n// Verify passphrase without unlocking\nconst isValid = await key.verify('newPass');\nconsole.log(isValid); // true",
        },
      ],
    },
    {
      title: "MnemonicJSON Format (Plaintext — Handle With Care)",
      description:
        "The MnemonicJSON format stores the raw seed words as an array — useful for offline/paper backup workflows, but not an at-rest storage format.",
      category: "Advanced",
      difficulty: "intermediate",
      examples: [
        {
          title: "Export and import MnemonicJSON",
          language: "typescript",
          code: "// ⚠️ jsonData.seed is the raw mnemonic in plaintext. Treat this exactly\n// like the mnemonic itself — encrypt the file yourself, or keep it offline.\nconst mnemonicJson = key.toMnemonicJSON(mnemonic);\n/*\n{\n  id: string,         // the account's encrypted backup blob\n  seed: string[],     // mnemonic as word array — PLAINTEXT\n  phrase?: string     // optional passphrase, if supplied — also plaintext\n}\n*/\n\n// Reconstruct from MnemonicJSON\nconst restored = await MajikKey.fromMnemonicJSON(\n  mnemonicJson,\n  'passphrase',\n  'Restored Device'\n);",
        },
      ],
    },
    {
      title: "Experimental Web3 Usage",
      description:
        "Accessing the experimental Bitcoin and Solana key material.",
      category: "Advanced",
      difficulty: "intermediate",
      examples: [
        {
          title: "Bitcoin WIF and Solana address",
          language: "typescript",
          code: "// Bitcoin — key material already derived and stored by default\nconst wif = key.getBitcoinWIF();\n\n// The real BIP-84 mainnet key instead of Majik's domain-separated default:\nconst standardBtc = await MajikKey.deriveStandardBitcoinFromMnemonic(mnemonic);\n\n// Solana — derived on demand from the Ed25519 signing key, no extra dependency\nconst solanaAddress = key.getSolanaAddress();\n\n// Real @solana/kit signer (requires @solana/kit installed)\nconst solanaSigner = await key.getSolanaKeypair();",
        },
      ],
    },
    {
      title: "Ecosystem Integration",
      description:
        "Converting a MajikKey to types used by the Majikah messaging stack.",
      category: "Integration",
      difficulty: "intermediate",
      examples: [
        {
          title: "Convert to MajikContact and MajikMessageIdentity",
          language: "typescript",
          code: "import { MajikKey } from '@majikah/majik-key';\nimport { MajikUser } from '@thezelijah/majik-user';\n\nconst key = await MajikKey.create(mnemonic, 'pass');\n\n// Convert to MajikContact (for local contact list, messaging SDK)\nconst contact = key.toContact();\nconst contactJson = await contact.toJSON();\n\n// Convert to MajikMessageIdentity (for relay registration)\nconst user = MajikUser.create({ id: 'user-uuid', displayName: 'Zelijah' });\nconst identity = await key.toMajikMessageIdentity(user, {\n  label: 'My Phone',\n  restricted: false\n});\nconst identityJson = identity.toJSON();\n// Send identityJson to your backend/relay",
        },
      ],
    },
  ],
  technicalSpecs: {
    title: "Technical Specifications",
    subsections: [
      {
        title: "Cryptographic Primitives",
        table: {
          headers: ["Primitive", "Library", "Usage"],
          rows: [
            {
              cells: [
                "Ed25519",
                "@stablelib/ed25519",
                "Seed-to-keypair derivation — used directly for signing, and converted to X25519 for the identity key",
              ],
            },
            {
              cells: [
                "X25519 (Curve25519)",
                "ed2curve + @stablelib/x25519",
                "Classical DH identity keypair",
              ],
            },
            {
              cells: [
                "ML-KEM-768 (Kyber)",
                "@noble/post-quantum",
                "Post-quantum KEM — NIST FIPS-203",
              ],
            },
            {
              cells: [
                "ML-DSA-87 (Dilithium)",
                "@noble/post-quantum",
                "Post-quantum signing — NIST FIPS-204",
              ],
            },
            {
              cells: [
                "secp256k1 / BIP-32",
                "@noble/curves + @scure/bip32",
                "@experimental Bitcoin HD key derivation (BIP-32/84)",
              ],
            },
            {
              cells: [
                "AES-256-GCM",
                "@stablelib/aes + @stablelib/gcm",
                "Symmetric encryption for every private key blob",
              ],
            },
            {
              cells: [
                "SHA-256",
                "@stablelib/sha256",
                "Public key fingerprinting, domain-separation hashes, integrity checks",
              ],
            },
            {
              cells: [
                "Argon2id (KDF v2)",
                "hash-wasm (WASM) + @noble/hashes (fallback)",
                "Passphrase and mnemonic key derivation",
              ],
            },
            {
              cells: [
                "PBKDF2-SHA256 (KDF v1)",
                "@stablelib/pbkdf2",
                "Legacy read-only: 250k iterations for passphrase, 200k for mnemonic",
              ],
            },
            {
              cells: [
                "BIP-39",
                "@scure/bip39",
                "Mnemonic generation and seed derivation, 10 supported languages",
              ],
            },
          ],
        },
      },
      {
        title: "Key Sizes",
        table: {
          headers: ["Key Material", "Size", "Format in JSON"],
          rows: [
            { cells: ["X25519 public key", "32 bytes", "base64 string"] },
            {
              cells: [
                "X25519 private key (encrypted)",
                "12 bytes IV + 48 bytes ciphertext = 60 bytes",
                "base64 string",
              ],
            },
            { cells: ["Ed25519 public key", "32 bytes", "base64 string"] },
            {
              cells: [
                "Ed25519 private key (encrypted)",
                "12 bytes IV + 80 bytes ciphertext = 92 bytes",
                "base64 string",
              ],
            },
            { cells: ["ML-KEM-768 public key", "1184 bytes", "base64 string"] },
            {
              cells: [
                "ML-KEM-768 secret key (encrypted)",
                "12 bytes IV + 2416 bytes ciphertext = 2428 bytes",
                "base64 string",
              ],
            },
            { cells: ["ML-DSA-87 public key", "2592 bytes", "base64 string"] },
            {
              cells: [
                "ML-DSA-87 secret key (encrypted)",
                "12 bytes IV + 4912 bytes ciphertext = 4924 bytes",
                "base64 string",
              ],
            },
            {
              cells: [
                "Bitcoin public key (compressed)",
                "33 bytes",
                "base64 string (@experimental)",
              ],
            },
            {
              cells: [
                "Bitcoin private key (encrypted)",
                "12 bytes IV + 48 bytes ciphertext = 60 bytes",
                "base64 string (@experimental)",
              ],
            },
            { cells: ["Salt", "32 bytes", "base64 string"] },
            { cells: ["AES-GCM IV", "12 bytes", "embedded in encrypted blob"] },
            {
              cells: [
                "Argon2id output (AES key)",
                "32 bytes",
                "ephemeral — never stored",
              ],
            },
          ],
        },
      },
      {
        title: "Argon2id Parameters",
        table: {
          headers: [
            "Context",
            "Memory (m)",
            "Passes (t)",
            "Parallelism (p)",
            "Output (dkLen)",
          ],
          rows: [
            {
              cells: [
                "Passphrase (every private key type)",
                "64 MB (65536 KB)",
                "3",
                "4",
                "32 bytes",
              ],
            },
            {
              cells: [
                "Mnemonic backup",
                "64 MB (65536 KB)",
                "3",
                "2",
                "32 bytes",
              ],
            },
          ],
        },
      },
      {
        title: "Encrypted Blob Format",
        description:
          "Every private key type follows the same layout: [12-byte IV][N-byte AES-GCM ciphertext, including a 16-byte GCM tag]. The IV is random and unique per blob. All blobs on an account share the same Argon2id-derived key (one KDF call, several encryptions).",
        list: [
          "X25519 blob: 12 (IV) + 32 (plaintext) + 16 (GCM tag) = 60 bytes",
          "Ed25519 blob: 12 (IV) + 64 (plaintext) + 16 (GCM tag) = 92 bytes",
          "ML-KEM-768 blob: 12 (IV) + 2400 (plaintext) + 16 (GCM tag) = 2428 bytes",
          "ML-DSA-87 blob: 12 (IV) + 4896 (plaintext) + 16 (GCM tag) = 4924 bytes",
          "Bitcoin blob (@experimental): 12 (IV) + 32 (plaintext) + 16 (GCM tag) = 60 bytes",
          "All blobs on the same account share the Argon2id-derived key but use different random IVs",
          "Changing the passphrase generates a fresh salt and re-derives the Argon2id key — every present blob is re-encrypted together",
        ],
      },
    ],
  },
  compatibility: {
    title: "Compatibility",
    description:
      "@majikah/majik-key is distributed as an ES module (type: module). It requires environments that support SubtleCrypto and Uint8Array, and falls back to a raw-bytes keypair representation where native WebCrypto X25519 support is unavailable.",
    runtime: [
      {
        name: "Node.js",
        supported: true,
        minVersion: "18",
        notes:
          "SubtleCrypto available via globalThis.crypto since Node 18. WASM (hash-wasm) works natively.",
      },
      {
        name: "Browser",
        supported: true,
        notes:
          "All modern browsers supported. SubtleCrypto required (available on all browsers with HTTPS). WASM accelerated.",
      },
      {
        name: "Electron",
        supported: true,
        notes: "Works in both main and renderer process. WASM available.",
      },
      {
        name: "Cloudflare Workers",
        supported: true,
        notes:
          "SubtleCrypto available. hash-wasm WASM probe returns false (no window), library falls back to @noble/hashes automatically. Enable nodejs_compat if using alongside firebase-admin.",
      },
      {
        name: "Bun",
        supported: true,
        notes:
          "Full compatibility. Bun supports SubtleCrypto and WASM natively.",
      },
      {
        name: "Deno",
        supported: true,
        notes:
          "Requires --allow-net and --allow-read for WASM loading. Falls back gracefully.",
      },
      {
        name: "React Native",
        supported: true,
        // TODO(Zelijah): the isomorphic (WebCrypto-or-raw-fallback) design
        // should make RN work, but I don't have direct evidence of it being
        // exercised on RN in the source I reviewed. Worth an explicit test
        // pass (especially the WASM probe path and crypto.getRandomValues
        // polyfill requirements) before advertising this as fully supported.
        notes:
          "Expected to work via the isomorphic raw-keypair fallback and a standard RN crypto polyfill (e.g. react-native-get-random-values), but not independently verified against the reviewed source — recommend a dedicated test pass before relying on this in production.",
      },
    ],
    typescript: {
      included: true,
      strictMode: true,
      declaration: true,
    },
  },
  migration: {
    title: "Migration Guide",
    description:
      "Upgrading accounts from legacy PBKDF2 (kdfVersion: 1) to Argon2id + the full modern keyset (kdfVersion: 2).",
    guides: [
      {
        fromVersion: "kdfVersion: 1 (PBKDF2, no ML-KEM/Ed25519/ML-DSA)",
        toVersion:
          "kdfVersion: 2 (Argon2id + ML-KEM-768 + Ed25519 + ML-DSA-87)",
        breakingChanges: [],
        steps: [
          "Detect upgrade need: load the stored JSON, check key.isFullyUpgraded (returns false for old accounts)",
          "Prompt the user for their mnemonic — it is required to re-derive the newer keypairs deterministically",
          "Call MajikKey.importFromMnemonicBackup(key.backup, mnemonic, passphrase, label)",
          "Persist the upgraded JSON: await myStorage.save('identity', upgraded.toJSON())",
          "The upgraded key has the same fingerprint/ID as the old key — no identity change",
        ],
        codeComparison: {
          before: {
            language: "typescript",
            code: "// Old account — PBKDF2, no post-quantum keys\nconst old = MajikKey.fromJSON(storedJson);\nconsole.log(old.kdfVersion);       // 1\nconsole.log(old.hasMlKem);         // false\nconsole.log(old.hasSigningKeys);   // false\nconsole.log(old.isFullyUpgraded);  // false",
          },
          after: {
            language: "typescript",
            code: "// After importFromMnemonicBackup\nconst upgraded = await MajikKey.importFromMnemonicBackup(\n  old.backup, mnemonic, passphrase\n);\nconsole.log(upgraded.kdfVersion);       // 2\nconsole.log(upgraded.hasMlKem);         // true\nconsole.log(upgraded.hasSigningKeys);   // true\nconsole.log(upgraded.isFullyUpgraded);  // true\nconsole.log(upgraded.fingerprint === old.fingerprint); // true — same identity",
            caption:
              "The mnemonic is the source of truth. Re-derivation guarantees the full keyset is always present after migration, regardless of when the original account was created.",
          },
        },
      },
    ],
  },
  bestPractices: {
    title: "Best Practices",
    description:
      "Recommendations for integrating MajikKey securely into your application.",
    practices: [
      {
        title: "Lock keys when not in use",
        description:
          "The lock/unlock model exists to minimize private key exposure time. Always lock after you're done with private key operations.",
        dos: [
          "Call key.lock() immediately after completing any private key operation",
          "Keep the unlock window as short as possible",
          "Consider unlocking only within the scope of a single function",
        ],
        donts: [
          "Don't hold references to private key bytes longer than necessary",
          "Don't keep a MajikKey in the unlocked state indefinitely",
          "Don't store the passphrase in memory alongside the key",
        ],
        category: "Security",
      },
      {
        title: "Persist the passphrase-encrypted JSON, not raw keys",
        description:
          "toJSON() produces a serialization that is safe to store in any database. Every private key is encrypted — only the passphrase can unlock them.",
        dos: [
          "Store key.toJSON() in your database, IndexedDB, Supabase, etc.",
          "Treat the JSON as opaque blob storage — you don't need to query individual fields",
        ],
        donts: [
          "Don't store getPrivateKeyBase64(), getMlKemSecretKey(), getEdSecretKey(), getMlDsaSecretKey(), or getBtcSecretKey() output anywhere",
          "Don't log key.toString() to application logs",
        ],
        category: "Security",
      },
      {
        title: "Never confuse toMnemonicJSON() with toJSON()",
        description:
          "toMnemonicJSON() is a plaintext transport format for the mnemonic itself — it is not the safe, encrypted-at-rest format that toJSON()/toString() produce.",
        dos: [
          "Only call toMnemonicJSON() for a one-time, protected export (e.g. into a file you encrypt yourself, or a paper backup)",
          "Discard/overwrite the resulting object as soon as the export is complete",
        ],
        donts: [
          "Don't store the output of toMnemonicJSON() unencrypted in a database or filesystem",
          "Don't confuse it with toJSON()'s output when writing storage code — they are not interchangeable",
        ],
        category: "Security",
      },
      {
        title: "Reserve toDangerousJSON() for server-side secret injection",
        description:
          "toDangerousJSON() / fromDangerousJSON() skip encryption entirely by design — instant reconstruction of a fully unlocked key from raw bytes.",
        dos: [
          "Use it only for injecting a pre-unlocked signing key into a trusted server process (e.g. from a secrets manager at boot)",
        ],
        donts: [
          "Don't use it for anything that touches a client, a database, a log, or the network",
        ],
        category: "Security",
      },
      {
        title: "Show mnemonic exactly once during onboarding",
        description:
          "The mnemonic is the master recovery key. Display it to the user during account creation, confirm they've backed it up, then never display it again.",
        dos: [
          "Show the mnemonic on a dedicated screen with copy/write-down affordances",
          "Ask the user to confirm a few random words before proceeding",
          "Store only key.toJSON() after onboarding — not the mnemonic",
        ],
        donts: [
          "Don't store the mnemonic in your database",
          "Don't send the mnemonic over any network",
          "Don't display the mnemonic in logs or error messages",
        ],
        category: "Security",
      },
      {
        title: "Use importFromMnemonicBackup() for cross-device restores",
        description:
          "Always use this method for account recovery and new device setup. It guarantees the identity is fully upgraded regardless of when it was originally created.",
        dos: [
          "Use importFromMnemonicBackup() for both new-device setup and legacy migration",
          "Allow the user to set a new passphrase per device — passphrase is device-specific, mnemonic is identity-wide",
        ],
        donts: [
          "Don't copy the raw JSON between devices as your primary transfer mechanism — use the backup blob + mnemonic instead",
          "Don't assume the backup blob is current — always re-derive via importFromMnemonicBackup()",
        ],
        category: "Security",
      },
      {
        title: "Treat Web3 (Bitcoin/Solana) support as experimental",
        description:
          "The web3 namespace and related methods are marked @experimental and may change without a major version bump.",
        dos: [
          "Review the derivation paths and threat model carefully before relying on this for production custody of real funds",
          "Pin your dependency version if you rely on Web3 methods, given the experimental status",
        ],
        donts: [
          "Don't assume API stability on anything under the web3 namespace across minor version bumps",
        ],
        category: "Security",
      },
      {
        title: "Handle Argon2id latency gracefully in your UI",
        description:
          "Argon2id is intentionally slow. Operations like create(), unlock(), updatePassphrase(), and importFromMnemonicBackup() take noticeably longer than a typical function call — expect this to vary meaningfully by device and by whether WASM acceleration is available.",
        dos: [
          "Show a spinner or progress indicator during any MajikKey async operation",
          "Run these operations off the main UI thread if possible (Web Worker, background thread)",
          "Benchmark on your actual target devices rather than assuming a fixed number — WASM availability and device class both matter a lot here",
        ],
        donts: [
          "Don't call these in a context that assumes near-instant resolution",
          "Don't treat a slow first call as a bug — WASM probing happens once per session",
        ],
        category: "Performance",
      },
    ],
  },
  troubleshooting: {
    title: "Troubleshooting",
    commonIssues: [
      {
        problem:
          "unlock() throws 'Decryption failed — incorrect passphrase or corrupted data'",
        symptoms: [
          "MajikKeyError thrown from unlock()",
          "isUnlocked remains false after the call",
        ],
        causes: [
          "Wrong passphrase supplied",
          "JSON was corrupted during storage or transfer",
          "kdfVersion mismatch — JSON has kdfVersion: 2 but an old code path used PBKDF2 to decrypt",
        ],
        solutions: [
          "Verify the passphrase with key.verify(passphrase) before unlocking — returns boolean without throwing",
          "Check key.kdfVersion in the JSON matches what you expect (1 = PBKDF2 legacy, 2 = Argon2id)",
          "If the JSON was base64-encoded at the transport layer, ensure it was decoded correctly before JSON.parse",
        ],
      },
      {
        problem:
          "getMlKemSecretKey() / getEdSecretKey() / getMlDsaSecretKey() throws 'No ... secret key'",
        symptoms: [
          "key.hasMlKem or key.hasSigningKeys is false",
          "key.isFullyUpgraded is false",
        ],
        causes: [
          "The JSON was created by an older version of the library before that key type was introduced",
          "The account was loaded via fromJSON() from a pre-upgrade snapshot",
        ],
        solutions: [
          "Run the full migration: call MajikKey.importFromMnemonicBackup() with the user's mnemonic",
          "Check key.isFullyUpgraded / key.hasSigningKeys before attempting to access these keys, and prompt migration if false",
        ],
      },
      {
        problem: "getBitcoinWIF() / web3.bitcoin is undefined",
        symptoms: [
          "key.hasBitcoin is false, or key.web3?.bitcoin is undefined even when unlocked",
        ],
        causes: [
          "The account predates Web3 support and hasn't been re-imported via importFromMnemonicBackup()",
          // TODO(Zelijah): update this line once the deriveBitcoin opt-out
          // shape is finalized — accounts created with that option set to
          // skip Bitcoin will also legitimately have no Bitcoin key here.
          "The account was created with the Bitcoin derivation explicitly opted out (if applicable in your version)",
        ],
        solutions: [
          "Re-import via MajikKey.importFromMnemonicBackup() to derive the missing key",
          "Check key.hasBitcoin before calling getBitcoinWIF() or accessing key.web3?.bitcoin",
        ],
      },
      {
        problem:
          "MajikKeyError: '@scure/btc-signer is required...' or '@solana/kit is required...'",
        symptoms: [
          "Thrown from getBitcoinAddress() / toBitcoinAddress(), or from getSolanaKeypair()",
        ],
        causes: [
          "The optional peer dependency for that chain isn't installed — these are lazy-loaded, not bundled",
        ],
        solutions: [
          "npm install @scure/btc-signer for Bitcoin address encoding/PSBTs",
          "npm install @solana/kit for real Solana signer/address objects",
          "Note: WIF export, raw key access, and base58 Solana addresses don't need either dependency",
        ],
      },
      {
        problem: "WASM unavailable warning in console",
        symptoms: [
          "[majikah/crypto] hash-wasm unavailable, using @noble/hashes argon2id fallback logged to console",
        ],
        causes: [
          "Running in an environment without WASM support (some Cloudflare Workers configurations, restricted environments)",
          "hash-wasm WASM module failed to load",
        ],
        solutions: [
          "This is handled automatically — @noble/hashes provides an identical fallback. No action required.",
          "Expect the KDF step to take noticeably longer without WASM. Still functionally correct — output is bit-identical either way.",
          "In Cloudflare Workers, this is expected and normal behavior.",
        ],
      },
      {
        problem:
          "importFromMnemonicBackup() throws 'Failed to decrypt backup — invalid mnemonic'",
        symptoms: ["MajikKeyError thrown from importFromMnemonicBackup()"],
        causes: [
          "The mnemonic provided does not match the one used to create the backup",
          "The backup blob is corrupted or truncated",
          "Word order is wrong or a word is misspelled",
        ],
        solutions: [
          "Verify the mnemonic with MajikKey.validateMnemonic(mnemonic) first — returns boolean",
          "Check the backup is a complete, unmodified base64 string",
          "If the user typed the mnemonic, ensure no auto-correct modifications occurred",
        ],
      },
      {
        problem: "mlKemSecretKey getter returns undefined instead of throwing",
        symptoms: [
          "Code checking `if (key.mlKemSecretKey)` behaves unexpectedly on a locked or unmigrated account",
        ],
        causes: [
          "The plain mlKemSecretKey getter is deprecated precisely because it returns undefined silently instead of throwing",
        ],
        solutions: [
          "Use getMlKemSecretKey() instead — it throws a MajikKeyError with a clear message on both a locked key and a pre-migration account",
        ],
      },
    ],
    debugging: [
      {
        title: "Inspecting key state without unlocking",
        description:
          "Use the metadata getter for a safe read-only summary of the key state — no key bytes, encrypted or otherwise.",
        code: {
          language: "typescript",
          code: "const meta = key.metadata;\n/*\n{\n  id: string,\n  fingerprint: string,\n  label: string,\n  timestamp: Date,\n  isLocked: boolean,\n  kdfVersion: number,        // 1 or 2\n  hasMlKem: boolean,\n  web3: { hasBitcoin?: boolean, hasSolana?: boolean },\n  mnemonicLanguage?: string\n}\n*/",
        },
      },
      {
        title: "Verify passphrase without unlocking",
        description:
          "Use verify() to check the passphrase before a full unlock — useful for UI validation.",
        code: {
          language: "typescript",
          code: "const isCorrect = await key.verify('passphrase');\n// Returns true/false without throwing or leaving the key unlocked",
        },
      },
    ],
  },
  ecosystem: {
    title: "Ecosystem",
    paragraphs: [
      "@majikah/majik-key is the identity foundation of the Majikah SDK stack. It handles key generation, signing, and management — other Majikah packages handle messaging, contacts, and relay communication.",
      "The library is built on well-audited cryptographic primitives from the noble and stablelib families, all widely used in the Web3 and security communities.",
    ],
    relatedPackages: [
      {
        name: "@majikah/majik-message",
        description:
          "Post-quantum E2EE messaging SDK. Uses MajikKey identities to encrypt and decrypt messages via ML-KEM encapsulation and X25519 DH.",
        url: "https://www.npmjs.com/package/@majikah/majik-message",
        relationship: "complement",
      },
      {
        name: "@majikah/majik-signature",
        description:
          "Hybrid post-quantum file signing and verification, built on MajikKey's Ed25519 and ML-DSA-87 signing keypairs.",
        url: "https://www.npmjs.com/package/@majikah/majik-signature",
        relationship: "complement",
      },
      {
        name: "@majikah/majik-contact",
        description:
          "Contact representation used across the Majikah messaging SDK. toContact() constructs one of these directly from a MajikKey.",
        url: "https://www.npmjs.com/package/@majikah/majik-contact",
        relationship: "dependency",
      },
      {
        name: "@thezelijah/majik-user",
        description:
          "User profile model for the Majikah ecosystem. Used by toMajikMessageIdentity() to bind a MajikKey to a user account.",
        url: "https://www.npmjs.com/package/@thezelijah/majik-user",
        relationship: "peer",
      },
      {
        name: "@noble/post-quantum",
        description:
          "Audited pure-JS implementation of NIST post-quantum algorithms — ML-KEM-768 (FIPS-203) and ML-DSA-87 (FIPS-204). Powers all post-quantum keypair derivation in MajikKey.",
        url: "https://www.npmjs.com/package/@noble/post-quantum",
        relationship: "dependency",
      },
      {
        name: "@noble/hashes",
        description:
          "Audited pure-JS cryptographic hash functions. Provides the Argon2id fallback when hash-wasm WASM is unavailable.",
        url: "https://www.npmjs.com/package/@noble/hashes",
        relationship: "dependency",
      },
      {
        name: "@noble/curves",
        description:
          "Audited pure-JS elliptic curve implementations, including secp256k1/Schnorr. Powers the experimental Bitcoin signing and public key derivation.",
        url: "https://www.npmjs.com/package/@noble/curves",
        relationship: "dependency",
      },
      {
        name: "@scure/bip39",
        description:
          "BIP-39 mnemonic generation and seed derivation. Used for generateMnemonic() and mnemonicToSeed().",
        url: "https://www.npmjs.com/package/@scure/bip39",
        relationship: "dependency",
      },
      {
        name: "@scure/bip32",
        description:
          "BIP-32 hierarchical deterministic key derivation. Powers the experimental Bitcoin (BIP-32/84) key derivation off the raw seed.",
        url: "https://www.npmjs.com/package/@scure/bip32",
        relationship: "dependency",
      },
      {
        name: "hash-wasm",
        description:
          "WASM-accelerated hash functions including Argon2id. Used as the primary KDF implementation, with @noble/hashes as the fallback.",
        url: "https://www.npmjs.com/package/hash-wasm",
        relationship: "dependency",
      },
      {
        name: "@scure/btc-signer",
        description:
          "Optional peer dependency. Only required for native SegWit Bitcoin address encoding and PSBT construction — not for key derivation, WIF export, or signing.",
        url: "https://www.npmjs.com/package/@scure/btc-signer",
        relationship: "optional",
      },
      {
        name: "@solana/kit",
        description:
          "Optional peer dependency. Only required for real @solana/kit KeyPairSigner instances and the kit-native Address type — not for base58 address derivation or raw key access.",
        url: "https://www.npmjs.com/package/@solana/kit",
        relationship: "optional",
      },
    ],
  },
  support: {
    title: "Support & Resources",
    documentation: [
      {
        label: "GitHub Repository",
        url: "https://github.com/Majikah/majik-key",
        type: "reference",
      },
      {
        label: "npm Package",
        url: "https://www.npmjs.com/package/@majikah/majik-key",
        type: "reference",
      },
      {
        label: "Security Policy",
        url: "https://github.com/Majikah/majik-key/blob/main/SECURITY.md",
        type: "reference",
      },
    ],
    community: [
      {
        platform: "GitHub Issues",
        url: "https://github.com/Majikah/majik-key/issues",
      },
      {
        platform: "GitHub Sponsors",
        url: "https://github.com/sponsors/jedlsf",
      },
    ],
    issues: {
      url: "https://github.com/Majikah/majik-key/issues",
    },
    contributing:
      "https://github.com/Majikah/majik-key/blob/main/CONTRIBUTING.md",
  },
  cta: {
    title: "Start Building with Majik Key",
    description:
      "Add post-quantum identity — encryption, hybrid signing, and experimental multi-chain keys — to your app in minutes. One install, one mnemonic, a full cryptographic identity, fully recoverable, fully yours.",
    actions: [
      {
        label: "npm install @majikah/majik-key",
        url: "https://www.npmjs.com/package/@majikah/majik-key",
        type: "install",
      },
      {
        label: "View on GitHub",
        url: "https://github.com/Majikah/majik-key",
        type: "github",
      },
    ],
    closingStatement:
      "Built by Zelijah for the Majikah ecosystem. Open source under Apache-2.0.",
  },
  // TODO(Zelijah): timestamp/lastUpdate carried over unchanged from the
  // previous version of this page data — update these to reflect when this
  // refactor actually ships, and reconcile with the `version` field above.
  timestamp: new Date("2026-02-11").toISOString(),
  lastUpdate: new Date("2026-02-11").toISOString(),
  version: "0.3.0",
  isEnabled: true,
};
